import { useEffect, useMemo, useState, type DragEvent, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Input, Select, Switch, Textarea } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { apiBlobUrl } from '@/data/api';
import { WriteBoard } from '@/components/WriteBoard';
import { WritePageModal } from '@/components/WritePageModal';
import { PageOutlineEditor } from '@/components/PageOutlineEditor';
import {
  SITE_MAP_KEY, SITE_MAP_EXPORT_PATH, getSiteMap, migrateSiteMap, createSitePage, updateSitePage, retireSitePage, restoreSitePage,
  previewMoveSitePage, moveSitePage, renameSitePage, importSiteMapXml, importSiteMapPaste,
  syncSiteMapRepo, addRepoPageToSiteMap, ignoreRepoRoute, acceptRepoRename, scaffoldSiteMap, crawlSitePage,
  getSitePageQuestions, suggestSitePageQuestions, addSitePageQuestion, removeSitePageQuestion, markSitePageQuestionsSent, checkSitePageQuestions, clearSitePageQuestions, getInterviewPlan, askFaqPage, requestPageInterview,
  getSiteMapLinks, backfillSiteMapLinks, getSiteMapRedirects, pushSiteMapRedirects, syncSiteMapXml, nameSiteMapFromUrls,
  suggestSitePageBrief, getScaffoldPrompt,
  type SitePage, type SiteMapTree, type RenamePlan, type SiteMapImportPreview, type PlanGroup,
} from '@/data/client';

/* The site map as a tree a person can edit: name, slug, type, keywords and
   brief per page, drag to reorder or re-parent, imports from sitemap.xml or
   a pasted plan previewed before they land, and a Markdown export for a
   Claude Code session. Admin only, scoped to the active org.

   Moving a page under a different parent changes its route, and a route is
   the key in nine tables, so that move is previewed (what will change, what
   it touches) and confirmed before the Worker runs the cascade. A reorder
   among siblings is just a reorder. */

const TYPES = ['home', 'service', 'location', 'about', 'contact', 'other'];
const STATUS_BADGE: Record<string, { label: string; variant: 'success' | 'accent' | 'warning' | 'neutral' | 'outline' }> = {
  planned: { label: 'Planned', variant: 'neutral' },
  scaffolded: { label: 'Scaffolded', variant: 'warning' },
  built: { label: 'Built', variant: 'accent' },
  live: { label: 'Live', variant: 'success' },
  retired: { label: 'Retired', variant: 'outline' },
};
const errMsg = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);
const leaf = (route: string) => route.replace(/^\/|\/$/g, '').split('/').pop() || '';

type Drop = { id: string; where: 'before' | 'after' | 'inside' } | null;

export function SitemapBuilder() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: SITE_MAP_KEY, queryFn: getSiteMap });
  const refresh = () => qc.invalidateQueries({ queryKey: SITE_MAP_KEY });

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [showRetired, setShowRetired] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [addingUnder, setAddingUnder] = useState<string | null | undefined>(undefined); // undefined = closed, null = root
  const [importing, setImporting] = useState<'xml' | 'paste' | null>(null);
  const [planOpen, setPlanOpen] = useState(false);
  const [linksOpen, setLinksOpen] = useState(false);
  const [redirectsOpen, setRedirectsOpen] = useState(false);
  const [boardOpen, setBoardOpen] = useState(false);
  const [writing, setWriting] = useState<SitePage | null>(null);
  const [pending, setPending] = useState<{ plan: RenamePlan; run: () => Promise<void> } | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [drop, setDrop] = useState<Drop>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');

  const pages = data?.pages ?? [];
  const byRoute = useMemo(() => new Map(pages.map((p) => [p.route, p])), [pages]);
  const children = useMemo(() => {
    const m = new Map<string | null, SitePage[]>();
    for (const p of pages) {
      const key = p.parentRoute && byRoute.has(p.parentRoute) ? p.parentRoute : null;
      m.set(key, [...(m.get(key) || []), p]);
    }
    return m;
  }, [pages, byRoute]);

  // The flat, visible list in tree order with depth, honouring collapse and the retired filter.
  const visible = useMemo(() => {
    const out: { page: SitePage; depth: number; hasKids: boolean }[] = [];
    const walk = (parent: string | null, depth: number) => {
      for (const p of children.get(parent) || []) {
        if (p.status === 'retired' && !showRetired) continue;
        const kids = (children.get(p.route) || []).filter((k) => showRetired || k.status !== 'retired');
        out.push({ page: p, depth, hasKids: kids.length > 0 });
        if (!collapsed.has(p.route)) walk(p.route, depth + 1);
      }
    };
    walk(null, 0);
    return out;
  }, [children, collapsed, showRetired]);

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(true); setErr(''); setNote('');
    try { await fn(); refresh(); if (label) setNote(label); }
    catch (e) { setErr(errMsg(e, 'That did not work.')); }
    finally { setBusy(false); }
  };

  /* A move: same parent → reorder now; new parent → preview, confirm, cascade. */
  const requestMove = async (page: SitePage, parentRoute: string | null, index: number) => {
    const sameParent = (page.parentRoute ?? null) === (parentRoute ?? null);
    const apply = () => run('Moved.', () => moveSitePage(page.id, parentRoute, index));
    if (sameParent) { await apply(); return; }
    setBusy(true); setErr('');
    try {
      const { rename } = await previewMoveSitePage(page.id, parentRoute);
      if (!rename) { await apply(); return; }
      setPending({ plan: rename, run: apply });
    } catch (e) { setErr(errMsg(e, 'Could not plan that move.')); }
    finally { setBusy(false); }
  };

  const requestRename = async (page: SitePage, to: string) => {
    setBusy(true); setErr('');
    try {
      const { rename } = await renameSitePage(page.id, to, true);
      setPending({ plan: rename, run: () => run('Renamed.', () => renameSitePage(page.id, to, false)) });
    } catch (e) { setErr(errMsg(e, 'Could not plan that rename.')); }
    finally { setBusy(false); }
  };

  // Keyboard moves, for anyone not dragging.
  const siblingsOf = (p: SitePage) => (children.get(p.parentRoute && byRoute.has(p.parentRoute) ? p.parentRoute : null) || []).filter((s) => showRetired || s.status !== 'retired');
  const moveBy = (p: SitePage, delta: number) => {
    const sibs = siblingsOf(p);
    const i = sibs.findIndex((s) => s.id === p.id);
    const to = i + delta;
    if (i < 0 || to < 0 || to >= sibs.length) return;
    void requestMove(p, p.parentRoute ?? null, to);
  };
  const indent = (p: SitePage) => {
    const sibs = siblingsOf(p);
    const i = sibs.findIndex((s) => s.id === p.id);
    if (i <= 0) return;
    void requestMove(p, sibs[i - 1].route, (children.get(sibs[i - 1].route) || []).length);
  };
  const outdent = (p: SitePage) => {
    if (!p.parentRoute) return;
    const parent = byRoute.get(p.parentRoute);
    if (!parent) return;
    const gp = parent.parentRoute ?? null;
    const i = siblingsOf(parent).findIndex((s) => s.id === parent.id);
    void requestMove(p, gp, i + 1);
  };

  // Drag and drop (native). The pointer's position in the row decides before / inside / after.
  const onDragOver = (e: DragEvent<HTMLLIElement>, target: SitePage) => {
    if (!dragId || dragId === target.id) return;
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    const y = (e.clientY - r.top) / r.height;
    const where = target.route === '/' ? 'after' : y < 0.25 ? 'before' : y > 0.75 ? 'after' : 'inside';
    if (!drop || drop.id !== target.id || drop.where !== where) setDrop({ id: target.id, where });
  };
  const onDrop = (target: SitePage) => {
    const moving = pages.find((p) => p.id === dragId);
    const d = drop;
    setDragId(null); setDrop(null);
    if (!moving || !d || moving.id === target.id || target.route.startsWith(moving.route)) return;
    if (d.where === 'inside') { void requestMove(moving, target.route, (children.get(target.route) || []).length); return; }
    const parent = target.parentRoute && byRoute.has(target.parentRoute) ? target.parentRoute : null;
    const sibs = (children.get(parent) || []).filter((s) => s.id !== moving.id);
    const ti = sibs.findIndex((s) => s.id === target.id);
    void requestMove(moving, parent, d.where === 'before' ? ti : ti + 1);
  };

  const download = async () => {
    setBusy(true); setErr('');
    try {
      const url = await apiBlobUrl(SITE_MAP_EXPORT_PATH);
      const a = document.createElement('a');
      a.href = url; a.download = 'site-map.md'; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setErr(errMsg(e, 'Could not export.')); }
    finally { setBusy(false); }
  };

  if (isLoading) return <Card padding="lg" className="smb"><p className="dash-muted">Loading the site map…</p></Card>;
  if (error || !data) return <Card padding="lg" className="smb"><p className="dash-muted">{errMsg(error, 'Could not load the site map.')}</p></Card>;

  const t: SiteMapTree = data;
  const live = pages.filter((p) => p.status !== 'retired');

  return (
    <Card padding={false} className="dash-leads smb">
      <div className="dash-panelhead dash-panelhead--pad">
        <div>
          <h2><Icon name="layers" size={17} /> Site map</h2>
          <p>
            {t.migrated
              ? <>{live.length} page{live.length === 1 ? '' : 's'}{Object.entries(t.counts).filter(([k]) => k !== 'retired').map(([k, v]) => ` · ${v} ${k}`).join('')} · links build on <code>{t.origin || 'no origin — set the website in Profile'}</code>{t.stagingUrl ? ' (staging)' : ''}</>
              : 'Every page of the client\'s site, with its name, slug, type, keywords and brief. The generators, the FAQ picker and Page Health read from here.'}
          </p>
        </div>
        <div className="smb__headactions">
          {t.migrated && t.repo && <Button variant="outline" size="sm" disabled={busy} onClick={() => run('', async () => { const r = await syncSiteMapRepo(); setNote(syncLine(r.sync)); })} iconLeft={<Icon name="refresh-cw" size={14} />}>Sync from repo</Button>}
          {t.migrated && t.repo && <Button variant={boardOpen ? 'primary' : 'outline'} size="sm" disabled={busy} onClick={() => setBoardOpen((v) => !v)} iconLeft={<Icon name="edit" size={14} />}>Write site</Button>}
          {t.migrated && <Button variant="outline" size="sm" disabled={busy} onClick={() => setLinksOpen(true)} iconLeft={<Icon name="link" size={14} />}>Links</Button>}
          {t.migrated && t.repo && <Button variant="outline" size="sm" disabled={busy} onClick={() => setRedirectsOpen(true)} iconLeft={<Icon name="repeat" size={14} />}>Redirects</Button>}
          {t.migrated && !t.stagingUrl && t.origin && <Button variant="outline" size="sm" disabled={busy} onClick={() => run('', async () => { const r = await syncSiteMapXml(); setNote(r.xml.error ? `sitemap.xml: ${r.xml.error}` : `sitemap.xml read: ${r.xml.added.length} new page${r.xml.added.length === 1 ? '' : 's'}, ${r.xml.missing.length} no longer listed${r.xml.crawled ? `, ${r.xml.crawled} crawled into the Vault` : ''}.`); })} iconLeft={<Icon name="globe" size={14} />}>Check sitemap.xml</Button>}
          {t.migrated && <Button variant="outline" size="sm" disabled={busy} onClick={() => setPlanOpen(true)} iconLeft={<Icon name="message-circle" size={14} />}>Interview plan</Button>}
          {t.migrated && <Button variant="outline" size="sm" disabled={busy} onClick={download} iconLeft={<Icon name="download" size={14} />}>Export handoff</Button>}
          <Button variant="outline" size="sm" disabled={busy} onClick={() => setImporting('xml')} iconLeft={<Icon name="globe" size={14} />}>Import sitemap.xml</Button>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => setImporting('paste')} iconLeft={<Icon name="list" size={14} />}>Paste a plan</Button>
          <Button variant="primary" size="sm" disabled={busy} onClick={() => { setAddingUnder(null); setEditing(null); }} iconLeft={<Icon name="plus" size={14} />}>Add page</Button>
        </div>
      </div>

      {(note || err) && <p className={`gen-note smb__note${err ? ' is-err' : ''}`}>{err || note}</p>}

      {!t.migrated && (
        <div className="smb__empty">
          {t.legacyRows > 0 ? (
            <>
              <p>This client's sitemap is still the text in the Vault ({t.legacyRows} source{t.legacyRows === 1 ? '' : 's'}). Bring it in here to edit it as pages.</p>
              <Button variant="primary" size="sm" disabled={busy} onClick={() => run('Migrated.', migrateSiteMap)}>Migrate the Vault sitemap</Button>
            </>
          ) : (
            <p>No pages yet. Paste a plan from the Sheet, import the live sitemap.xml, or add pages one at a time.</p>
          )}
        </div>
      )}

      {addingUnder !== undefined && (
        <AddPageForm
          parentRoute={addingUnder}
          parents={live}
          busy={busy}
          onCancel={() => setAddingUnder(undefined)}
          onSave={(input) => run('Page added.', async () => { await createSitePage(input); setAddingUnder(undefined); })}
        />
      )}

      {boardOpen && <WriteBoard onClose={() => setBoardOpen(false)} />}

      {!boardOpen && t.migrated && t.xml && (
        <div className="smb__tools">
          <span className="dash-muted">
            sitemap.xml checked {t.xml.at.slice(0, 16).replace('T', ' ')}{t.xml.error ? ` — ${t.xml.error}` : `: ${t.xml.added.length} new, ${t.xml.missing.length} no longer listed${t.xml.excluded ? `, ${t.xml.excluded} skipped by the crawl filter` : ''}`}. Rechecked every hour for live sites.
          </span>
        </div>
      )}
      {!boardOpen && t.migrated && (
        <div className="smb__tools">
          <label className="smb__toggle"><input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} /> Show retired</label>
          <span className="dash-muted">Drag the handle to reorder or nest. Moving under another page changes its URL; you will see what that touches first.</span>
          <button type="button" className="smb__link" disabled={busy} title="Pages named from the crawl become their URL's name; the headline moves to keywords. Names you typed stay." onClick={() => { if (confirm('Name every page from its URL? Only names still taken from the crawl change; the old headline moves to Keywords where empty. Names you typed in stay.')) void run('', async () => { const r = await nameSiteMapFromUrls(); setNote(`${r.renamed} page${r.renamed === 1 ? '' : 's'} renamed from their URLs${r.seeded ? `, ${r.seeded} headline${r.seeded === 1 ? '' : 's'} moved to keywords` : ''}${r.kept ? `, ${r.kept} typed name${r.kept === 1 ? '' : 's'} kept` : ''}.`); }); }}>Name pages from URLs</button>
        </div>
      )}

      {!boardOpen && t.migrated && t.repo && (
        <DriftPanel
          tree={t}
          busy={busy}
          onScaffold={(routes) => run('', async () => { const r = await scaffoldSiteMap(routes); setNote(`${r.pushed.length} page${r.pushed.length === 1 ? '' : 's'} scaffolded${r.skipped.length ? `, ${r.skipped.length} already had a file` : ''}${r.remaining ? `, ${r.remaining} more to go — press again` : ''}.`); })}
          onAdd={(u) => run(`Added ${u.route.replace(/\/$/, '')} from the repo.`, () => addRepoPageToSiteMap(u.route, u.path))}
          onIgnore={(route, ignored) => run(ignored ? 'Ignored.' : 'Back in the list.', () => ignoreRepoRoute(route, ignored))}
          onRename={async (r) => {
            setBusy(true); setErr('');
            try {
              const { rename } = await acceptRepoRename(r.from, r.to, r.path, true);
              setPending({ plan: rename, run: () => run('Renamed to match the repo.', () => acceptRepoRename(r.from, r.to, r.path, false)) });
            } catch (e) { setErr(errMsg(e, 'Could not plan that rename.')); }
            finally { setBusy(false); }
          }}
        />
      )}

      {!boardOpen && visible.length > 0 && (
        <ul className="smb__tree" onDragLeave={() => setDrop(null)}>
          {visible.map(({ page: p, depth, hasKids }) => {
            const isEditing = editing === p.id;
            const d = drop && drop.id === p.id ? drop.where : null;
            return (
              <li
                key={p.id}
                className={`smb__row${d ? ` is-drop-${d}` : ''}${dragId === p.id ? ' is-dragging' : ''}${p.status === 'retired' ? ' is-retired' : ''}`}
                style={{ ['--depth' as string]: depth }}
                onDragOver={(e) => onDragOver(e, p)}
                onDrop={(e) => { e.preventDefault(); onDrop(p); }}
              >
                <div className="smb__main">
                  <span
                    className="smb__grip"
                    draggable={p.route !== '/'}
                    title={p.route === '/' ? 'Home stays first' : 'Drag to move'}
                    onDragStart={(e) => { setDragId(p.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', p.id); }}
                    onDragEnd={() => { setDragId(null); setDrop(null); }}
                  ><Icon name="grip" size={14} /></span>
                  <button type="button" className={`smb__twisty${hasKids ? '' : ' is-leaf'}`} onClick={() => hasKids && setCollapsed((c) => { const n = new Set(c); n.has(p.route) ? n.delete(p.route) : n.add(p.route); return n; })} aria-label={collapsed.has(p.route) ? 'Expand' : 'Collapse'}>
                    {hasKids && <Icon name={collapsed.has(p.route) ? 'chevron-right' : 'chevron-down'} size={14} />}
                  </button>
                  <div className="smb__name">
                    <strong>{p.title || leaf(p.route) || 'Home'}</strong>
                    <code>{p.route === '/' ? '/' : p.route.replace(/\/$/, '')}</code>
                  </div>
                  <div className="smb__badges">
                    <Badge variant={STATUS_BADGE[p.status]?.variant ?? 'neutral'}>{STATUS_BADGE[p.status]?.label ?? p.status}</Badge>
                    {p.pageType && p.pageType !== 'other' && <span className="smb__type">{p.pageType}</span>}
                    {p.focusRank != null && <Badge variant="accent">Focus #{p.focusRank}</Badge>}
                    {t.readiness[p.route] && p.status !== 'retired' && (
                      <span title={`${t.readiness[p.route].service}: ${t.readiness[p.route].covered.length} of ${t.readiness[p.route].covered.length + t.readiness[p.route].open.length} interview slots covered`}>
                        <Badge variant={t.readiness[p.route].state === 'ready' ? 'success' : 'warning'}>
                          {t.readiness[p.route].state === 'ready' ? 'Ready to write' : t.readiness[p.route].state === 'thin' ? 'Thin interviews' : 'No interviews'}
                        </Badge>
                      </span>
                    )}
                    {p.hidden && <span className="smb__flag" title="Hidden from client-facing pickers"><Icon name="eye-off" size={12} /></span>}
                    {p.brief && <span className="smb__flag" title="Has a brief"><Icon name="file-text" size={12} /></span>}
                    {!p.title && <span className="smb__flag smb__flag--warn" title="No title yet">no title</span>}
                    {p.missingSince && <Badge variant="warning">File missing since {p.missingSince.slice(0, 10)}</Badge>}
                    {!p.missingSince && t.repo && t.sync && p.status === 'planned' && !p.repoPath && <span className="smb__flag smb__flag--warn" title="No file in the repo yet">no file</span>}
                  </div>
                  <div className="smb__actions">
                    <button type="button" className="smb__ibtn" title="Move up" disabled={busy} onClick={() => moveBy(p, -1)}><Icon name="chevron-down" size={13} style={{ transform: 'rotate(180deg)' }} /></button>
                    <button type="button" className="smb__ibtn" title="Move down" disabled={busy} onClick={() => moveBy(p, 1)}><Icon name="chevron-down" size={13} /></button>
                    <button type="button" className="smb__ibtn" title="Nest under the page above" disabled={busy || p.route === '/'} onClick={() => indent(p)}><Icon name="chevron-right" size={13} /></button>
                    <button type="button" className="smb__ibtn" title="Move out one level" disabled={busy || !p.parentRoute} onClick={() => outdent(p)}><Icon name="chevron-left" size={13} /></button>
                    <Button variant="ghost" size="sm" disabled={busy} onClick={() => { setAddingUnder(p.route); setEditing(null); }} title="Add a page under this one"><Icon name="plus" size={13} /></Button>
                    <Button variant={isEditing ? 'outline' : 'ghost'} size="sm" disabled={busy} onClick={() => { setEditing(isEditing ? null : p.id); setAddingUnder(undefined); }}>{isEditing ? 'Close' : 'Edit'}</Button>
                  </div>
                </div>
                {isEditing && (
                  <PageEditor
                    page={p}
                    busy={busy}
                    hasChildren={(children.get(p.route) || []).length > 0}
                    onSave={(patch) => run('Saved.', () => updateSitePage(p.id, patch))}
                    onRename={(to) => requestRename(p, to)}
                    onRetire={() => run('Retired.', async () => { await retireSitePage(p.id); setEditing(null); })}
                    onRestore={() => run('Restored.', () => restoreSitePage(p.id))}
                    questions={<PageQuestions page={p} busy={busy} onNote={(m) => { setErr(''); setNote(m); }} onErr={setErr} />}
                    outline={<PageOutlineEditor page={p} busy={busy} onNote={(m) => { setErr(''); setNote(m); }} onErr={setErr} />}
                    onCrawl={t.origin ? () => run('', async () => { const r = await crawlSitePage(p.id); setNote(`${r.result === 'updated' ? 'Re-crawled' : 'Crawled'} ${r.url} into the Vault.`); }) : undefined}
                    onScaffold={t.repo && p.status === 'planned' && !p.repoPath ? () => run('', async () => { const r = await scaffoldSiteMap([p.route]); setNote(r.pushed.length ? `Scaffolded ${r.pushed[0].path}.` : 'That page already has a file.'); }) : undefined}
                    onCopyPrompt={t.repo && p.status === 'planned' && !p.repoPath ? () => run('', async () => { const r = await getScaffoldPrompt(p.id); await navigator.clipboard.writeText(r.prompt); setNote('Copied the Claude Code prompt for this page. Once Claude Code commits the scaffold, press Sync from repo.'); }) : undefined}
                    onRescaffold={t.repo && p.status === 'scaffolded' ? () => run('', async () => { const r = await getScaffoldPrompt(p.id, true); await navigator.clipboard.writeText(r.prompt); setNote('Copied the re-scaffold prompt. It keeps the page above the fold and rebuilds the rest from the outline; press Sync from repo once Claude Code commits it.'); }) : undefined}
                    onWrite={p.status === 'scaffolded' && t.repo ? () => setWriting(p) : undefined}
                    onSuggestBrief={() => run('', async () => { const r = await suggestSitePageBrief(p.id); setNote(r.filled.length ? `Suggested ${r.filled.join(', ')} for ${p.title || p.route}. Review it and save the page.` : 'Nothing to suggest: this page already has a type, keywords and a brief.'); })}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}

      {writing && <WritePageModal pageId={writing.id} name={writing.title || writing.route} staging={!!t.stagingUrl} onClose={() => setWriting(null)} />}
      {planOpen && <InterviewPlanModal onClose={() => setPlanOpen(false)} onNote={(m) => { setErr(''); setNote(m); }} />}
      {linksOpen && <LinksModal onClose={() => setLinksOpen(false)} />}
      {redirectsOpen && <RedirectsModal onClose={() => setRedirectsOpen(false)} onNote={(m) => { setErr(''); setNote(m); }} />}

      {importing && (
        <ImportModal
          kind={importing}
          defaultDomain={t.origin || ''}
          onClose={() => setImporting(null)}
          onDone={(msg) => { setImporting(null); refresh(); setNote(msg); }}
        />
      )}

      {pending && (
        <div className="admin-modal" role="dialog" aria-modal="true">
          <div className="admin-modal__scrim" onClick={() => setPending(null)} />
          <div className="admin-modal__panel">
            <div className="admin-modal__head">
              <div><h3>Change this page's URL?</h3><p className="dash-muted">Everything that names the old route moves with it, and the old route keeps resolving.</p></div>
            </div>
            <ul className="smb__moves">
              {pending.plan.moves.map((m) => <li key={m.from}><code>{m.from.replace(/\/$/, '') || '/'}</code> → <code>{m.to.replace(/\/$/, '') || '/'}</code></li>)}
            </ul>
            <p className="dash-muted smb__affected">
              {describeAffected(pending.plan.affected)}
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="ghost" size="sm" onClick={() => setPending(null)}>Cancel</Button>
              <Button variant="primary" size="sm" disabled={busy} onClick={async () => { const r = pending.run; setPending(null); await r(); }}>Move it</Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function syncLine(s: SiteMapTree['sync']): string {
  if (!s) return '';
  return `Synced ${s.at.slice(0, 16).replace('T', ' ')}: ${s.files} page file${s.files === 1 ? '' : 's'}, ${s.matched} matched, ${s.missing.length} without a file, ${s.unplanned.length} unplanned, ${s.renames.length} likely rename${s.renames.length === 1 ? '' : 's'}${s.truncated ? ' · the repo tree was truncated by GitHub, so some files were not seen' : ''}.`;
}

/* What the repo and the plan disagree on, with the action for each. */
function DriftPanel({ tree, busy, onScaffold, onAdd, onIgnore, onRename }: {
  tree: SiteMapTree; busy: boolean;
  onScaffold: (routes?: string[]) => void;
  onAdd: (u: { route: string; path: string }) => void;
  onIgnore: (route: string, ignored: boolean) => void;
  onRename: (r: { from: string; to: string; path: string }) => void;
}) {
  const [showIgnored, setShowIgnored] = useState(false);
  const s = tree.sync;
  if (!s) {
    return <div className="smb__drift"><span className="dash-muted">Repo <code>{tree.repo!.repo}</code> on <code>{tree.repo!.branch}</code>, pages under <code>{tree.repo!.pagesPath}</code>. Press Sync from repo to compare.</span></div>;
  }
  const clean = s.missing.length === 0 && s.unplanned.length === 0 && s.renames.length === 0;
  return (
    <div className="smb__drift">
      <div className="smb__driftline">
        {clean ? <Badge variant="success" dot>In sync with the repo</Badge> : <Badge variant="warning">Out of sync</Badge>}
        <span className="dash-muted">{syncLine(s)}</span>
      </div>
      {s.renames.length > 0 && (
        <div className="smb__driftgroup">
          <h5>Likely renamed in the repo</h5>
          <ul>{s.renames.map((r) => (
            <li key={r.from}><code>{r.from.replace(/\/$/, '')}</code> → <code>{r.to.replace(/\/$/, '')}</code> <span className="dash-muted">({r.path})</span>
              <Button variant="outline" size="sm" disabled={busy} onClick={() => onRename(r)}>Accept…</Button></li>
          ))}</ul>
        </div>
      )}
      {s.missing.length > 0 && (
        <div className="smb__driftgroup">
          <h5>Planned, no file yet ({s.missing.length})</h5>
          <p className="dash-muted">{s.missing.slice(0, 12).map((r) => r.replace(/\/$/, '')).join(', ')}{s.missing.length > 12 ? `, … ${s.missing.length - 12} more` : ''}</p>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => onScaffold()} iconLeft={<Icon name="file-text" size={13} />}>Scaffold {Math.min(s.missing.length, 25)} page{Math.min(s.missing.length, 25) === 1 ? '' : 's'} from the templates</Button>
        </div>
      )}
      {s.unplanned.length > 0 && (
        <div className="smb__driftgroup">
          <h5>In the repo, not in the plan ({s.unplanned.length})</h5>
          <ul>{s.unplanned.map((u) => (
            <li key={u.route}><code>{u.route.replace(/\/$/, '') || '/'}</code> <span className="dash-muted">({u.path})</span>
              <Button variant="outline" size="sm" disabled={busy} onClick={() => onAdd(u)}>Add to plan</Button>
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => onIgnore(u.route, true)}>Ignore</Button></li>
          ))}</ul>
        </div>
      )}
      {tree.ignored.length > 0 && (
        <div className="smb__driftgroup">
          <button type="button" className="smb__link" onClick={() => setShowIgnored((v) => !v)}>{tree.ignored.length} ignored file{tree.ignored.length === 1 ? '' : 's'} {showIgnored ? '▾' : '▸'}</button>
          {showIgnored && <ul>{tree.ignored.map((r) => <li key={r}><code>{r.replace(/\/$/, '')}</code> <Button variant="ghost" size="sm" disabled={busy} onClick={() => onIgnore(r, false)}>Stop ignoring</Button></li>)}</ul>}
        </div>
      )}
    </div>
  );
}

/* The page's question ledger. Generated questions can be removed, the
   team's own added, and every question carries its state: open, sent,
   answered (with the answer and where it came from). The Vault check runs
   on request and, from the sweep, on its own when a transcript lands; an
   answered question is closed for good, its slot marked covered, and an
   faq-routed answer filed on the page for review. */
function PageQuestions({ page, busy, onNote, onErr }: { page: SitePage; busy: boolean; onNote: (m: string) => void; onErr: (m: string) => void }) {
  const qc = useQueryClient();
  const key = ['siteMap', 'questions', page.id];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => getSitePageQuestions(page.id) });
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const [working, setWorking] = useState<string | null>(null);
  const [newQ, setNewQ] = useState('');
  const [newTo, setNewTo] = useState<'interview' | 'faq'>('interview');
  const [showAnswered, setShowAnswered] = useState(true);
  const qs = data?.questions ?? [];
  const open = qs.filter((q) => q.state === 'open');
  const answered = qs.filter((q) => q.state === 'answered');
  const sent = qs.filter((q) => q.state === 'sent');
  const chosen = picked ?? new Set(open.map((q) => q.id));
  const toggle = (id: string) => setPicked((cur) => { const n = new Set(cur ?? open.map((x) => x.id)); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selected = open.filter((q) => chosen.has(q.id));
  const put = (r: PageQuestionSetLike) => { qc.setQueryData(key, r); qc.invalidateQueries({ queryKey: SITE_MAP_KEY }); };
  const act = async (label: string, fn: () => Promise<PageQuestionSetLike>, note?: (r: PageQuestionSetLike) => string) => {
    setWorking(label); onErr('');
    try { const r = await fn(); put(r); setPicked(null); if (note) onNote(note(r)); }
    catch (e) { onErr(errMsg(e, 'That did not work.')); }
    finally { setWorking(null); }
  };
  const send = async (how: 'ask' | 'interview') => {
    setWorking(how); onErr('');
    try {
      if (how === 'ask') {
        const r = await askFaqPage(page.route, selected.map((q) => q.q));
        onNote(`Sent ${r.sent} question${r.sent === 1 ? '' : 's'} to the client for ${page.title || page.route}${r.drafted ? `, ${r.drafted} drafted from the Vault for them to confirm` : ''}.`);
      } else {
        const r = await requestPageInterview({ page: page.route, title: `Interview for ${page.title || page.route}`, questions: selected.map((q) => ({ q: q.q, route: q.routeTo })) });
        onNote(`Interview requested: "${r.title}". It is in the topic inbox with these questions attached.`);
      }
      put(await markSitePageQuestionsSent(page.id, selected.map((q) => q.id)));
      setPicked(null);
    } catch (e) { onErr(errMsg(e, 'Could not send.')); }
    finally { setWorking(null); }
  };
  if (isLoading) return <p className="dash-muted smb__meta">Loading this page's questions…</p>;
  if (!data) return null;
  const cov = data.coverage;
  const checkedLine = data.checkedAt
    ? `Checked against the Vault ${data.checkedAt.slice(0, 16).replace('T', ' ')}${data.stale ? ' — the Vault has changed since; answers are rechecked within minutes, and Check the Vault now also writes new follow-ups' : ''}`
    : 'Not checked against the Vault yet';
  return (
    <div className="smb__questions">
      <h5>
        What this page still needs answered
        <span className="dash-muted"> · {open.length} open{sent.length ? `, ${sent.length} sent` : ''}{answered.length ? `, ${answered.length} answered` : ''}</span>
        {cov && <span className="dash-muted"> · {cov.service}: {cov.covered.length} of {cov.covered.length + cov.open.length} interview slots covered</span>}
      </h5>
      <p className="dash-muted smb__meta" style={{ marginTop: 0 }}>{checkedLine}</p>
      <div className="smb__formactions" style={{ marginTop: 0, marginBottom: 8 }}>
        <Button variant="ghost" size="sm" disabled={busy || !!working || (data.missing.length > 0 && !open.length && !sent.length)} onClick={() => act('check', () => checkSitePageQuestions(page.id), checkNote)} iconLeft={<Icon name="search" size={13} />}>{working === 'check' ? 'Checking…' : 'Check the Vault now'}</Button>
        <Button variant="outline" size="sm" disabled={busy || !!working || data.missing.length > 0} onClick={() => act('suggest', () => suggestSitePageQuestions(page.id), (r) => `${r.questions.filter((q) => q.state === 'open').length} questions written for ${page.title || page.route}; your own and any answered ones were kept.`)} iconLeft={<Icon name="sparkles" size={13} />}>{working === 'suggest' ? 'Writing…' : 'Write questions for this page'}</Button>
        {data.missing.length > 0 && <span className="dash-muted smb__meta" style={{ margin: 0 }}>To write questions for this page, or follow-ups from the Vault, give it {data.missing.join(', ')} and save.</span>}
        {open.some((q) => q.source === 'generic') && <button type="button" className="smb__link" disabled={busy || !!working} onClick={() => act('clear', () => clearSitePageQuestions(page.id), () => 'Generated questions cleared; your own stay.')}>Clear the generic questions</button>}
      </div>
      {!qs.length && <p className="dash-muted smb__meta">No questions yet. Give the page a name, a type, and keywords or a brief, then check it against the Vault for follow-ups or write questions for it, or add your own below.</p>}
      {open.length > 0 && (
        <ul>
          {open.map((q) => (
            <li key={q.id}>
              <label><input type="checkbox" checked={chosen.has(q.id)} onChange={() => toggle(q.id)} /> {q.q}</label>
              <span className="smb__qwhy">{q.routeTo === 'faq' ? 'FAQ' : 'interview'}{q.why ? ` · ${q.why}` : ''}{q.buildsOn ? ` · follows up on ${q.buildsOn}` : ''}{q.source === 'custom' ? ' · yours' : ''}</span>
              <button type="button" className="smb__ibtn" title="Remove this question" disabled={busy || !!working} onClick={() => act('remove', () => removeSitePageQuestion(page.id, q.id))}><Icon name="x" size={12} /></button>
            </li>
          ))}
        </ul>
      )}
      {sent.length > 0 && (
        <ul className="smb__qsent">
          {sent.map((q) => <li key={q.id}><span className="dash-muted">{q.q}</span><span className="smb__qwhy">sent · waiting for the answer</span></li>)}
        </ul>
      )}
      <div className="smb__addq">
        <Input value={newQ} onChange={(e) => setNewQ(e.target.value)} placeholder="Add your own question…" onKeyDown={(e) => { if (e.key === 'Enter' && newQ.trim()) { void act('add', () => addSitePageQuestion(page.id, newQ.trim(), newTo)); setNewQ(''); } }} />
        <Select value={newTo} onChange={(e) => setNewTo(e.target.value as 'interview' | 'faq')} options={[{ value: 'interview', label: 'Interview answer' }, { value: 'faq', label: 'Page FAQ' }]} />
        <Button variant="outline" size="sm" disabled={busy || !!working || !newQ.trim()} onClick={() => { void act('add', () => addSitePageQuestion(page.id, newQ.trim(), newTo)); setNewQ(''); }}>Add</Button>
      </div>
      {open.length > 0 && (
        <div className="smb__formactions">
          <Button variant="outline" size="sm" disabled={busy || !!working || !selected.length} onClick={() => send('ask')} iconLeft={<Icon name="mail" size={13} />}>Ask the client ({selected.length})</Button>
          <Button variant="outline" size="sm" disabled={busy || !!working || !selected.length || selected.length > 14} onClick={() => send('interview')} iconLeft={<Icon name="message-circle" size={13} />}>Request an interview ({selected.length})</Button>
          <span className="dash-muted smb__meta" style={{ margin: 0 }}>Ask = short answers that become page FAQs. Interview = the long answers that ground the page{selected.length > 14 ? '; pick at most 14 for one interview' : ''}.</span>
        </div>
      )}
      {answered.length > 0 && (
        <div className="smb__answered">
          <button type="button" className="smb__link" onClick={() => setShowAnswered((v) => !v)}>{answered.length} answered {showAnswered ? '▾' : '▸'}</button>
          {showAnswered && (
            <ul>
              {answered.map((q) => (
                <li key={q.id}>
                  <strong>{q.q}</strong>
                  <span className="smb__qknown">{(q.answer || '').slice(0, 260)}{(q.answer || '').length > 260 ? '…' : ''}</span>
                  <span className="smb__qwhy">{q.answerSource ? `from ${q.answerSource}` : 'from the Vault'}{q.faqSourceId ? ' · filed as a page FAQ' : ''}{q.slot ? ` · slot covered: ${q.why || q.slot}` : ''}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
type PageQuestionSetLike = Awaited<ReturnType<typeof getSitePageQuestions>>;

/** The one line after Check the Vault now: what each of its three steps did. */
function checkNote(r: PageQuestionSetLike): string {
  const x = r.result;
  if (!x) return 'Checked.';
  const parts: string[] = [];
  if (x.audited) parts.push('the service was re-audited first');
  parts.push(x.checked ? `${x.answered} of ${x.checked} answered by the Vault` : 'nothing was waiting to be answered');
  if (x.slotsCovered) parts.push(`${x.slotsCovered} interview slot${x.slotsCovered === 1 ? '' : 's'} marked covered`);
  if (x.faqsFiled) parts.push(`${x.faqsFiled} filed as page FAQs for review`);
  if (x.followups) parts.push(`${x.followups} follow-up question${x.followups === 1 ? '' : 's'} written`);
  const s = parts.join(', ');
  return `${s.charAt(0).toUpperCase()}${s.slice(1)}.`;
}

/* Links that no longer land where they should, by decision a report: the
   articles are fixed by hand. The index fills at every push; the backfill
   reads every article body already on record. */
function LinksModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ['siteMap', 'links'], queryFn: getSiteMapLinks });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const backfill = async () => {
    setBusy(true); setNote('');
    try { const r = await backfillSiteMapLinks(); qc.setQueryData(['siteMap', 'links'], r.report); setNote(`Indexed ${r.links} link${r.links === 1 ? '' : 's'} across ${r.articles} article${r.articles === 1 ? '' : 's'}.`); }
    catch (e) { setNote(errMsg(e, 'Could not index the articles.')); }
    finally { setBusy(false); }
  };
  const broken = data?.broken ?? [];
  return (
    <div className="admin-modal" role="dialog" aria-modal="true">
      <div className="admin-modal__scrim" onClick={onClose} />
      <div className="admin-modal__panel" style={{ maxWidth: 820 }}>
        <div className="admin-modal__head">
          <div><h3>Links to fix</h3><p className="dash-muted">Articles and pages whose internal links point at a page that moved, was retired, or is not in the plan. Fix them in the article; nothing here rewrites a live site.</p></div>
        </div>
        {isLoading && <p className="dash-muted">Reading the index…</p>}
        {error && <p className="gen-note is-err">{errMsg(error, 'Could not read the link index.')}</p>}
        {data && (
          <p className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>{data.total} link{data.total === 1 ? '' : 's'} indexed from {data.indexed} piece{data.indexed === 1 ? '' : 's'} · {broken.length} to fix</p>
        )}
        {data && !broken.length && <p className="dash-muted">Every indexed link lands on a page that exists.</p>}
        {broken.length > 0 && (
          <ul className="smb__links">
            {broken.map((b) => (
              <li key={`${b.fromKind}:${b.fromId}:${b.toRoute}`}>
                <strong>{b.siteUrl ? <a href={b.siteUrl} target="_blank" rel="noreferrer">{b.title}</a> : b.title}</strong>
                <span className="dash-muted"> {b.fromKind === 'page' ? 'site page' : b.onSite ? 'on the site' : 'not pushed yet'}</span>
                <div><code>{b.toRoute.replace(/\/$/, '') || '/'}</code> {b.reason === 'moved' && b.nowRoute ? <>→ now <code>{b.nowRoute.replace(/\/$/, '')}</code></> : b.reason === 'retired' ? '— page retired' : '— not a page in the plan'}</div>
              </li>
            ))}
          </ul>
        )}
        {note && <p className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>{note}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <Button variant="outline" size="sm" disabled={busy} onClick={backfill}>{busy ? 'Indexing…' : 'Index the articles on record'}</Button>
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}

/* data/redirects.json in the client repo, with one entry per moved page
   that is not already there. Shown before it is pushed; existing entries
   are never changed. */
function RedirectsModal({ onClose, onNote }: { onClose: () => void; onNote: (m: string) => void }) {
  const { data, isLoading, error } = useQuery({ queryKey: ['siteMap', 'redirects'], queryFn: getSiteMapRedirects });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const push = async () => {
    setBusy(true); setErr('');
    try { const r = await pushSiteMapRedirects(); onNote(`${r.added} redirect${r.added === 1 ? '' : 's'} added to ${data?.path}.`); onClose(); }
    catch (e) { setErr(errMsg(e, 'Could not push the redirects.')); }
    finally { setBusy(false); }
  };
  return (
    <div className="admin-modal" role="dialog" aria-modal="true">
      <div className="admin-modal__scrim" onClick={onClose} />
      <div className="admin-modal__panel">
        <div className="admin-modal__head">
          <div><h3>Redirects</h3><p className="dash-muted">Every moved page as a 301 in <code>data/redirects.json</code>; the build writes <code>public/_redirects</code> from it. Entries already there are kept as they are.</p></div>
        </div>
        {isLoading && <p className="dash-muted">Reading the repo…</p>}
        {error && <p className="gen-note is-err">{errMsg(error, 'Could not read the redirects file.')}</p>}
        {data && (
          <>
            <p className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>{data.exists ? `${data.current.length} redirect${data.current.length === 1 ? '' : 's'} in the file now` : 'The file does not exist yet; it will be created'} · {data.additions.length} to add</p>
            {data.additions.length > 0 && <ul className="smb__moves">{data.additions.map((r) => <li key={r.from}><code>{r.from}</code> → <code>{r.to}</code></li>)}</ul>}
            {!data.additions.length && <p className="dash-muted">Every moved page is already redirected.</p>}
          </>
        )}
        {err && <p className="gen-note is-err">{err}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" disabled={busy || !data || !data.additions.length} onClick={push}>{busy ? 'Pushing…' : `Push ${data?.additions.length ?? 0} redirect${(data?.additions.length ?? 0) === 1 ? '' : 's'}`}</Button>
        </div>
      </div>
    </div>
  );
}

/* Every page that is not ready, grouped so one conversation unlocks several. */
function InterviewPlanModal({ onClose, onNote }: { onClose: () => void; onNote: (m: string) => void }) {
  const { data, isLoading, error } = useQuery({ queryKey: ['siteMap', 'plan'], queryFn: getInterviewPlan });
  const [sending, setSending] = useState<string | null>(null);
  const [err, setErr] = useState('');
  const groups = data?.groups ?? [];
  const request = async (g: PlanGroup) => {
    const first = g.pages[0];
    setSending(g.service ?? g.pageType); setErr('');
    try {
      const r = await requestPageInterview({
        page: first.route,
        title: g.service ? `Interview: ${g.service}` : `Interview: ${g.pageType} pages`,
        questions: g.open.slice(0, 14).map((label) => ({ q: `For ${g.service || g.pageType}: ${label}`, route: 'interview' as const })),
      });
      onNote(`Interview requested: "${r.title}".`);
    } catch (e) { setErr(errMsg(e, 'Could not request that interview.')); }
    finally { setSending(null); }
  };
  return (
    <div className="admin-modal" role="dialog" aria-modal="true">
      <div className="admin-modal__scrim" onClick={onClose} />
      <div className="admin-modal__panel" style={{ maxWidth: 760 }}>
        <div className="admin-modal__head">
          <div>
            <h3>Interview plan</h3>
            <p className="dash-muted">Pages that are not ready to write, grouped by the service they share, with what the interviews have not covered yet. One conversation per group.</p>
          </div>
        </div>
        {isLoading && <p className="dash-muted">Reading the ledger…</p>}
        {error && <p className="gen-note is-err">{errMsg(error, 'Could not build the plan.')}</p>}
        {!isLoading && !groups.length && <p className="dash-muted">Nothing is waiting. Every service page is ready to write and every other page exists.</p>}
        <div className="dash-stack" style={{ gap: 12 }}>
          {groups.map((g) => (
            <div key={(g.service ?? '') + g.pageType} className="smb__plangroup">
              <div className="smb__planhead">
                <strong>{g.service ?? `${g.pageType} pages`}</strong>
                {g.state !== 'n/a' && <Badge variant={g.state === 'ready' ? 'success' : 'warning'}>{g.covered.length} of {g.covered.length + g.open.length} covered</Badge>}
                <span style={{ flex: 1 }} />
                {g.open.length > 0 && <Button variant="outline" size="sm" disabled={!!sending} onClick={() => request(g)}>{sending === (g.service ?? g.pageType) ? 'Requesting…' : 'Request an interview'}</Button>}
              </div>
              <p className="dash-muted" style={{ margin: '4px 0' }}>Unlocks: {g.pages.map((p) => p.title || p.route).join(', ')}</p>
              {g.open.length > 0
                ? <p style={{ margin: 0, fontSize: 'var(--text-xs)' }}>Still open: {g.open.join(' · ')}</p>
                : <p className="dash-muted" style={{ margin: 0, fontSize: 'var(--text-xs)' }}>No service ledger for these — open a page and use its questions.</p>}
            </div>
          ))}
        </div>
        {err && <p className="gen-note is-err">{err}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}><Button variant="ghost" size="sm" onClick={onClose}>Close</Button></div>
      </div>
    </div>
  );
}

function describeAffected(a: RenamePlan['affected']): string {
  const parts = [
    a.faqs ? `${a.faqs} FAQ placement${a.faqs === 1 ? '' : 's'}` : '',
    a.topics ? `${a.topics} topic${a.topics === 1 ? '' : 's'}` : '',
    a.health ? `${a.health} Page Health row${a.health === 1 ? '' : 's'}` : '',
    a.services ? `${a.services} service${a.services === 1 ? '' : 's'}` : '',
    a.blogTopics ? `${a.blogTopics} blog topic${a.blogTopics === 1 ? '' : 's'}` : '',
    a.articles ? `${a.articles} interview plan${a.articles === 1 ? '' : 's'}` : '',
    a.links ? `${a.links} article link${a.links === 1 ? '' : 's'} that will need fixing (see Links)` : '',
  ].filter(Boolean);
  return parts.length ? `Also updates ${parts.join(', ')}. Links already published on the site are not rewritten here.` : 'Nothing else refers to this route yet.';
}

function AddPageForm({ parentRoute, parents, busy, onCancel, onSave }: {
  parentRoute: string | null; parents: SitePage[]; busy: boolean; onCancel: () => void;
  onSave: (input: { parentRoute: string | null; title: string; slug: string; pageType: string; keywords: string; brief: string }) => void;
}) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [parent, setParent] = useState<string>(parentRoute ?? '');
  const [type, setType] = useState('');
  const [keywords, setKeywords] = useState('');
  const [brief, setBrief] = useState('');
  useEffect(() => { if (!slugTouched) setSlug(title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')); }, [title, slugTouched]);
  const isHome = !parent && (/^(home|index)$/i.test(slug) || (!slug && /^home$/i.test(title.trim())));
  const route = isHome ? '/' : `${parent ? parent.replace(/\/$/, '') : ''}/${slug || '…'}`;
  return (
    <div className="smb__form">
      <div className="smb__formgrid">
        <Input label="Page name" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Water Heaters" autoFocus />
        <Input label="Slug" value={slug} onChange={(e) => { setSlug(e.target.value); setSlugTouched(true); }} hint={isHome ? 'URL: / (the home page has no slug)' : `URL: ${route}`} />
        <Select label="Under" value={parent} onChange={(e) => setParent(e.target.value)} options={[{ value: '', label: 'Top level' }, ...parents.filter((p) => p.route !== '/').map((p) => ({ value: p.route, label: `${'  '.repeat(Math.max(0, p.route.split('/').length - 3))}${p.title || leaf(p.route)}` }))]} />
        <Select label="Type" value={type} onChange={(e) => setType(e.target.value)} options={[{ value: '', label: 'Guess from the URL' }, ...TYPES]} />
        <Input label="Keywords" value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="what this page should rank for" />
        <Textarea label="Brief" value={brief} onChange={(e) => setBrief(e.target.value)} rows={2} placeholder="What this page must cover — Triton's intent, not the client's voice" />
      </div>
      <div className="smb__formactions">
        <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" size="sm" disabled={busy || (!slug && !isHome)} onClick={() => onSave({ parentRoute: parent || null, title: title.trim(), slug, pageType: type, keywords, brief })}>Add page</Button>
      </div>
    </div>
  );
}

function PageEditor({ page, busy, hasChildren, onSave, onRename, onRetire, onRestore, onCrawl, onScaffold, onCopyPrompt, onRescaffold, onSuggestBrief, onWrite, questions, outline }: {
  page: SitePage; busy: boolean; hasChildren?: boolean;
  onSave: (patch: Parameters<typeof updateSitePage>[1]) => void;
  onRename: (to: string) => void; onRetire: () => void; onRestore: () => void;
  onCrawl?: () => void; onScaffold?: () => void; onCopyPrompt?: () => void; onRescaffold?: () => void; onSuggestBrief?: () => void; onWrite?: () => void; questions?: ReactNode; outline?: ReactNode;
}) {
  const [title, setTitle] = useState(page.title || '');
  const [slug, setSlug] = useState(leaf(page.route));
  const [type, setType] = useState(page.pageType || 'other');
  const [cascadeType, setCascadeType] = useState(false);
  const [status, setStatus] = useState<string>(page.status);
  const [hidden, setHidden] = useState(page.hidden);
  const [focus, setFocus] = useState(page.focusRank == null ? '' : String(page.focusRank));
  const [keywords, setKeywords] = useState(page.keywords || '');
  const [brief, setBrief] = useState(page.brief || '');
  const [anchors, setAnchors] = useState(page.anchors || '');
  useEffect(() => {
    setTitle(page.title || ''); setSlug(leaf(page.route)); setType(page.pageType || 'other'); setStatus(page.status); setHidden(page.hidden);
    setFocus(page.focusRank == null ? '' : String(page.focusRank)); setKeywords(page.keywords || ''); setBrief(page.brief || ''); setAnchors(page.anchors || '');
  }, [page]);
  const slugChanged = page.route !== '/' && slug !== leaf(page.route);
  const newRoute = slugChanged ? `${page.parentRoute ? page.parentRoute.replace(/\/$/, '') : ''}/${slug}/` : page.route;
  const retired = page.status === 'retired';
  return (
    <div className="smb__form smb__form--edit">
      <div className="smb__formgrid">
        <Input label="Page name" value={title} onChange={(e) => setTitle(e.target.value)} />
        <Input label="Slug" value={slug} disabled={page.route === '/'} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-'))} hint={slugChanged ? `Will move to ${newRoute.replace(/\/$/, '')} — saved separately, with a preview.` : 'Changing the slug changes the URL.'} />
        <div>
          <Select label="Type" value={type} onChange={(e) => setType(e.target.value)} options={TYPES} hint="Service pages get the full interview question set; the type was guessed from the URL at import." />
          {hasChildren && <label className="smb__toggle" style={{ marginTop: 6 }}><input type="checkbox" checked={cascadeType} onChange={(e) => setCascadeType(e.target.checked)} /> Also apply this type to every page under this one</label>}
        </div>
        {!retired && <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)} options={[{ value: 'planned', label: 'Planned — not built yet' }, { value: 'scaffolded', label: 'Scaffolded — file is still the template' }, { value: 'built', label: 'Built — written, in the repo' }, { value: 'live', label: 'Live — on the site' }]} hint="Sync sets these from the repo; set them by hand for a site without one, or to overrule the template check." />}
        <Select label="SEO focus rank" value={focus} onChange={(e) => setFocus(e.target.value)} options={[{ value: '', label: 'Not a focus page' }, ...[1, 2, 3, 4, 5, 6, 7].map((n) => ({ value: String(n), label: `#${n}` }))]} hint="One page per rank; mirrors the Profile's SEO focus pages." />
        <Input label="Keywords" value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="what this page should rank for" />
        <Input label="Link text options" value={anchors} onChange={(e) => setAnchors(e.target.value)} hint="Comma-separated anchor options the writers may use for links to this page." />
        <div className="smb__switch"><Switch label="Hide from client-facing pickers (Expert Studio FAQs)" checked={hidden} onChange={(e) => setHidden(e.target.checked)} /></div>
        <div style={{ gridColumn: '1 / -1' }}>
          <Textarea label="Brief" value={brief} onChange={(e) => setBrief(e.target.value)} rows={3} placeholder="What this page must cover, who it is for, what to avoid. Read by the page writer and the interview planner." />
        </div>
      </div>
      <div className="smb__formactions">
        {!retired && <Button variant="ghost" size="sm" disabled={busy} onClick={onRetire}>Retire page…</Button>}
        {retired && <Button variant="outline" size="sm" disabled={busy} onClick={onRestore}>Restore page</Button>}
        {!retired && onCrawl && <Button variant="ghost" size="sm" disabled={busy} onClick={onCrawl} iconLeft={<Icon name="download" size={13} />}>Crawl into Vault</Button>}
        {!retired && onScaffold && <Button variant="ghost" size="sm" disabled={busy} onClick={onScaffold} iconLeft={<Icon name="file-text" size={13} />}>Scaffold file</Button>}
        {!retired && onWrite && <Button variant="outline" size="sm" disabled={busy} onClick={onWrite} iconLeft={<Icon name="edit" size={13} />} title="Write this scaffolded page from the Vault; pushed on its own commit when the site is on staging">Write this page</Button>}
        {!retired && onCopyPrompt && <Button variant="ghost" size="sm" disabled={busy} onClick={onCopyPrompt} iconLeft={<Icon name="copy" size={13} />} title="A prompt for Claude Code to build this page's scaffold in the site's own design">Copy Claude Code prompt</Button>}
        {!retired && onRescaffold && <Button variant="ghost" size="sm" disabled={busy} onClick={onRescaffold} iconLeft={<Icon name="copy" size={13} />} title="A prompt for Claude Code to rebuild this scaffold below the fold from the page's current outline. Needs an outline.">Copy re-scaffold prompt</Button>}
        {!retired && onSuggestBrief && (!page.pageType || page.pageType === 'other' || !page.keywords || !page.brief) && <Button variant="ghost" size="sm" disabled={busy} onClick={onSuggestBrief} iconLeft={<Icon name="sparkles" size={13} />} title="Fills only the empty type, keywords and brief, from the plan and the Vault">Suggest a plan</Button>}
        <span style={{ flex: 1 }} />
        {slugChanged && <Button variant="outline" size="sm" disabled={busy || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)} onClick={() => onRename(newRoute)}>Preview URL change…</Button>}
        <Button variant="primary" size="sm" disabled={busy} onClick={() => onSave({
          title, pageType: type, cascadeType, keywords, brief, anchors, hidden,
          focusRank: focus === '' ? null : Number(focus),
          ...(retired || status === page.status ? {} : { status: status as 'planned' | 'live' }),
        })}>Save</Button>
      </div>
      {!retired && questions}
      {!retired && page.status !== 'built' && page.status !== 'live' && outline}
      {page.repoPath && <p className="dash-muted smb__meta">File: <code>{page.repoPath}</code>{page.lastSeenRepo ? ` · seen in the repo ${page.lastSeenRepo.slice(0, 10)}` : ''}</p>}
      {page.lastSeenXml && <p className="dash-muted smb__meta">Listed in sitemap.xml {page.lastSeenXml.slice(0, 10)}</p>}
    </div>
  );
}

function ImportModal({ kind, defaultDomain, onClose, onDone }: { kind: 'xml' | 'paste'; defaultDomain: string; onClose: () => void; onDone: (msg: string) => void }) {
  const [url, setUrl] = useState(defaultDomain ? `${defaultDomain.replace(/\/$/, '')}/sitemap.xml` : '');
  const [domain, setDomain] = useState(defaultDomain);
  const [text, setText] = useState('');
  const [preview, setPreview] = useState<SiteMapImportPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const go = async (apply: boolean) => {
    setBusy(true); setErr('');
    try {
      const r = kind === 'xml' ? await importSiteMapXml(url.trim(), apply) : await importSiteMapPaste(domain.trim(), text, apply);
      if (apply) onDone(`${r.added.length} page${r.added.length === 1 ? '' : 's'} added${r.missing.length ? `, ${r.missing.length} flagged missing` : ''}.`);
      else setPreview(r);
    } catch (e) { setErr(errMsg(e, 'Import failed.')); }
    finally { setBusy(false); }
  };
  return (
    <div className="admin-modal" role="dialog" aria-modal="true">
      <div className="admin-modal__scrim" onClick={onClose} />
      <div className="admin-modal__panel">
        <div className="admin-modal__head">
          <div>
            <h3>{kind === 'xml' ? 'Import sitemap.xml' : 'Paste a plan'}</h3>
            <p className="dash-muted">{kind === 'xml' ? 'The live site\'s own list. New pages arrive live; pages the site no longer lists are flagged, never removed. Blog and article URLs are kept out by the crawl filter.' : 'Rows from the Sheet: one page per line, tabs for nesting, notes in the columns to the right. New pages arrive planned, with the notes as their brief.'}</p>
          </div>
        </div>
        <div className="dash-stack" style={{ gap: 10 }}>
          {kind === 'xml'
            ? <Input label="Sitemap URL" value={url} onChange={(e) => { setUrl(e.target.value); setPreview(null); }} placeholder="https://www.example.com/sitemap.xml" />
            : <>
                <Input label="Site URL" value={domain} onChange={(e) => { setDomain(e.target.value); setPreview(null); }} placeholder="https://staging.example.com" hint="The host the planned pages will live on." />
                <Textarea label="Rows" value={text} onChange={(e) => { setText(e.target.value); setPreview(null); }} rows={8} placeholder={'Home\nPlumbing\n\tWater Heaters\tTank vs tankless, brands\n\tDrain Cleaning'} />
              </>}
          {preview && (
            <div className="smb__preview">
              <p><strong>{preview.added.length}</strong> new page{preview.added.length === 1 ? '' : 's'} · <strong>{preview.seen}</strong> already here{preview.missing.length ? <> · <strong>{preview.missing.length}</strong> live page{preview.missing.length === 1 ? '' : 's'} not listed any more</> : null}{preview.excluded ? ` · ${preview.excluded} skipped by the crawl filter` : ''}</p>
              {preview.added.length > 0 && <ul>{preview.added.slice(0, 40).map((r) => <li key={r}><code>{r.replace(/\/$/, '') || '/'}</code></li>)}{preview.added.length > 40 && <li>…and {preview.added.length - 40} more</li>}</ul>}
              {preview.missing.length > 0 && <p className="dash-muted">Will be flagged missing: {preview.missing.map((r) => r.replace(/\/$/, '')).join(', ')}</p>}
            </div>
          )}
          {err && <p className="gen-note is-err">{err}</p>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
            {!preview && <Button variant="primary" size="sm" disabled={busy || (kind === 'xml' ? !url.trim() : !domain.trim() || !text.trim())} onClick={() => go(false)}>{busy ? 'Reading…' : 'Preview'}</Button>}
            {preview && <Button variant="primary" size="sm" disabled={busy || (preview.added.length === 0 && preview.missing.length === 0 && preview.seen === 0)} onClick={() => go(true)}>{busy ? 'Importing…' : preview.added.length ? `Add ${preview.added.length} page${preview.added.length === 1 ? '' : 's'}` : 'Apply'}</Button>}
          </div>
        </div>
      </div>
    </div>
  );
}
